"use client"

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Menu, X, Phone, AlertTriangle } from "lucide-react"
import { useState, useEffect, useCallback } from "react"
import { cn } from "@/lib/utils"
import { ModeToggle } from "@/components/mode-toggle"

export function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    const closeMenu = useCallback(() => setIsMenuOpen(false), [])

    useEffect(() => {
        if (!isMenuOpen) return
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeMenu()
        }
        document.addEventListener("keydown", handleEscape)
        return () => document.removeEventListener("keydown", handleEscape)
    }, [isMenuOpen, closeMenu])

    return (
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="bg-primary text-primary-foreground text-xs sm:text-sm py-1.5">
                <div className="container flex items-center justify-center gap-2">
                    <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    <span>Fragen zur IT?</span>
                    <a href="tel:+491784912583" className="font-semibold hover:underline">0178 4912583</a>
                </div>
            </div>
            <div className="container flex h-16 items-center justify-between">
                <Link className="flex items-center gap-2 font-bold text-xl" href="/">
                    <span>Webcodez</span>
                </Link>
                <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
                    <Link className="transition-colors hover:text-primary" href="/#services">
                        Services
                    </Link>
                    <Link className="transition-colors hover:text-primary" href="/#about">
                        Über uns
                    </Link>
                    <Link className="transition-colors hover:text-primary" href="/referenzen">
                        Referenzen
                    </Link>
                    <Link className="transition-colors hover:text-primary" href="/blog">
                        Blog
                    </Link>
                    <Link className="transition-colors hover:text-primary" href="/#contact">
                        Kontakt
                    </Link>
                    <Link className="transition-colors hover:text-primary" href="/it-betreuung-russischsprachig">
                        IT auf Russisch
                    </Link>
                    <Link
                        className="inline-flex items-center gap-1.5 font-semibold text-destructive transition-colors hover:text-destructive/80"
                        href="/it-notfall"
                    >
                        <AlertTriangle className="h-4 w-4" />
                        IT-Notfall
                    </Link>
                    <Button asChild size="sm">
                        <Link href="/#contact">Beratung anfragen</Link>
                    </Button>
                    <ModeToggle />
                </nav>
                <div className="flex md:hidden items-center gap-2">
                    <ModeToggle />
                    <Button
                        className="md:hidden"
                        size="icon"
                        variant="ghost"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-expanded={isMenuOpen}
                        aria-controls="mobile-menu"
                        aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
                    >
                        {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </Button>
                </div>
            </div>
            {/* Mobile Menu */}
            <div
                id="mobile-menu"
                className={cn(
                    "md:hidden border-t bg-background overflow-hidden transition-all duration-200 ease-in-out",
                    isMenuOpen ? "max-h-[32rem] opacity-100" : "max-h-0 opacity-0"
                )}
            >
                <nav className="flex flex-col gap-1 p-4 text-sm font-medium">
                    <Link
                        className="transition-colors hover:text-primary py-3 min-h-[44px] flex items-center"
                        href="/#services"
                        onClick={closeMenu}
                    >
                        Services
                    </Link>
                    <Link
                        className="transition-colors hover:text-primary py-3 min-h-[44px] flex items-center"
                        href="/#about"
                        onClick={closeMenu}
                    >
                        Über uns
                    </Link>
                    <Link
                        className="transition-colors hover:text-primary py-3 min-h-[44px] flex items-center"
                        href="/referenzen"
                        onClick={closeMenu}
                    >
                        Referenzen
                    </Link>
                    <Link
                        className="transition-colors hover:text-primary py-3 min-h-[44px] flex items-center"
                        href="/blog"
                        onClick={closeMenu}
                    >
                        Blog
                    </Link>
                    <Link
                        className="transition-colors hover:text-primary py-3 min-h-[44px] flex items-center"
                        href="/#contact"
                        onClick={closeMenu}
                    >
                        Kontakt
                    </Link>
                    <Link
                        className="transition-colors hover:text-primary py-3 min-h-[44px] flex items-center"
                        href="/it-betreuung-russischsprachig"
                        onClick={closeMenu}
                    >
                        IT auf Russisch
                    </Link>
                    <Link
                        className="flex items-center gap-2 font-semibold text-destructive py-3 min-h-[44px] hover:text-destructive/80 transition-colors"
                        href="/it-notfall"
                        onClick={closeMenu}
                    >
                        <AlertTriangle className="h-4 w-4" />
                        IT-Notfall – Soforthilfe
                    </Link>
                    <Button asChild size="default" className="w-full mt-2 min-h-[44px]">
                        <Link href="/#contact" onClick={closeMenu}>
                            Beratung anfragen
                        </Link>
                    </Button>
                </nav>
            </div>
        </header>
    )
}
